import {
  ListActionsAndIssuesGraphResponse_Action,
  ListActionsAndIssuesGraphResponse_Action_Type,
  ListActionsAndIssuesGraphResponse_ActionLane,
} from '@wavingroup/aqora-v2-api/wavin/aqora/v2/aqora_service_pb';
import {
  SeriesXrangeOptions,
  XrangePointOptionsObject,
  YAxisOptions,
} from 'highcharts';
import { TFunction } from 'i18next';
import { dateToLocaleString } from '~/pages/Metrics/date-to-locale-string';
import { theme } from '~/shared/theme/theme';

export const ACTION_LANE_WIDTH = 16;

export class ActionsChartModel {
  chartData: XrangePointOptionsObject[] = [];

  chartHeight = 0;

  hasActions = false;

  constructor(actionLanes: ListActionsAndIssuesGraphResponse_ActionLane[]) {
    this.chartData = actionLanes
      .map((lane, index) =>
        lane.actions.map((action) => ({
          // Index has to be added to x and x2 because otherwise Highcharts treats
          // the ranges with same x1 and x2 as the same and tooltips don't work as intended
          x: (action.startTime?.toDate().getTime() || 0) + index,
          x2: (action.stopTime?.toDate().getTime() || 0) + index,
          y: 0,
          color: `${theme.palette.secondary.main}4D`,
          custom: {
            action,
          },
        })),
      )
      .flat();

    this.chartHeight = 2.5 * ACTION_LANE_WIDTH;
  }

  createActionsYAxis(t: TFunction): YAxisOptions {
    if (!this.chartData.length) {
      return {
        height: this.chartHeight,
        opposite: false,
        labels: {
          enabled: false,
        },
        categories: [],
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      };
    }
    return {
      height: this.chartHeight,
      opposite: false,
      title: {
        y: 5,
        offset: 0,
        style: {
          transform: 'translateX(20px)',
          color: theme.palette.primary.dark,
          fontSize: '14px',
          lineHeight: '14px',
        },
        text: t('metrics.actions.title'),
      },
      labels: {
        enabled: false,
      },
      categories: [t('metrics.actions.title')],
      plotBands: [
        {
          from: -0.5,
          to: 0.5,
          color: theme.palette.background.default,
        },
      ],
    };
  }

  createSeries(t: TFunction, yIndex: number): SeriesXrangeOptions {
    return {
      type: 'xrange',
      minPointLength: 3,
      data: this.chartData,
      yAxis: yIndex,
      pointWidth: ACTION_LANE_WIDTH,
      borderRadius: 0,
      showInNavigator: false,
      turboThreshold: 0,
      enableMouseTracking: false,
      states: {
        inactive: {
          opacity: 1,
        },
      },
      groupPadding: 0.5,
    };
  }

  public static getChartTooltip({
    action,
    t,
  }: {
    action: ListActionsAndIssuesGraphResponse_Action;
    t: TFunction;
  }): string {
    const volumeM3 = (action.volumeL / 1000).toFixed(2);
    const startDate = action.startTime?.toDate();
    const endDate = action.stopTime?.toDate();

    let text;
    switch (action.type) {
      case ListActionsAndIssuesGraphResponse_Action_Type.SUPPLY_TO:
        text = t('metrics.actions.tooltip.supply', {
          volumeM3,
          fromTitle: action.fromTitle,
          toTitle: action.toTitle,
        });
        break;
      case ListActionsAndIssuesGraphResponse_Action_Type.DUMP_FROM:
        text = t('metrics.actions.tooltip.drain', {
          volumeM3,
          fromTitle: action.fromTitle,
          toTitle: action.toTitle,
        });
        break;
      default:
        return '';
    }
    return `
          <div>
          ${text}
          <br />
          <span>🕒 ${dateToLocaleString(startDate)} → ${dateToLocaleString(
            endDate,
          )}</span>
          </div>
        `;
  }
}
